import React, { useState, useEffect } from "react";
import {
  FileSearch,
  Plus,
  Edit2,
  Trash2,
  Search,
  X,
  Loader2,
  RefreshCw,
  AlertTriangle,
  Shield,
  CheckCircle,
  Clock,
  AlertCircle,
  User,
  Calendar,
  Target,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Database,
  Globe,
  Lock,
  Users,
  FileText,
  CheckSquare,
  Info,
  Eye,
} from "lucide-react";
import { supabase } from "../lib/supabase";

// ============================================
// FONCTIONS SUPABASE
// ============================================

const loadEFVPFromDB = async (entrepriseId) => {
  const { data, error } = await supabase
    .from("efvp")
    .select("*")
    .eq("entreprise_id", entrepriseId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
};

const addEFVPToDB = async (efvpData) => {
  const { data, error } = await supabase
    .from("efvp")
    .insert([efvpData])
    .select()
    .single();

  if (error) throw error;
  return data;
};

const updateEFVPInDB = async (id, updates) => {
  const { data, error } = await supabase
    .from("efvp")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

const deleteEFVPFromDB = async (id) => {
  const { error } = await supabase.from("efvp").delete().eq("id", id);
  if (error) throw error;
  return true;
};

// ============================================
// OPTIONS ET CONSTANTES LOI 25
// ============================================

const TYPES_RP = [
  "Nom et coordonnées",
  "Date de naissance / Âge",
  "Numéro d'assurance sociale",
  "Numéro de permis de conduire",
  "Informations financières",
  "Informations bancaires",
  "Informations d'emploi",
  "Données de santé",
  "Données biométriques",
  "Opinions et préférences",
  "Historique d'achats",
  "Données de navigation web",
  "Géolocalisation",
  "Photos / Vidéos",
  "Adresse IP",
  "Identifiants en ligne",
];

const CATEGORIES_PERSONNES = [
  "Clients",
  "Employés",
  "Anciens employés",
  "Candidats à l'emploi",
  "Fournisseurs",
  "Partenaires d'affaires",
  "Mineurs (moins de 14 ans)",
  "Mineurs (14-17 ans)",
  "Patients",
  "Membres",
  "Visiteurs",
  "Abonnés",
];

const TYPES_DONNEES_SENSIBLES = [
  { id: "sante", label: "Données de santé", article: "Art. 12" },
  { id: "biometrique", label: "Données biométriques", article: "Art. 12" },
  { id: "genetique", label: "Données génétiques", article: "Art. 12" },
  { id: "ethnie", label: "Origine ethnique ou raciale", article: "Art. 12" },
  { id: "politique", label: "Opinions politiques", article: "Art. 12" },
  { id: "religion", label: "Convictions religieuses", article: "Art. 12" },
  { id: "syndicat", label: "Appartenance syndicale", article: "Art. 12" },
  { id: "sexuel", label: "Orientation sexuelle", article: "Art. 12" },
  { id: "mineur", label: "Données de mineurs", article: "Art. 12" },
  {
    id: "judiciaire",
    label: "Infractions / Condamnations",
    article: "Art. 12",
  },
];

const RISQUES_TYPES = [
  {
    id: "acces_non_autorise",
    label: "Accès non autorisé",
    desc: "Accès par des personnes non habilitées",
  },
  { id: "perte", label: "Perte de données", desc: "Perte accidentelle ou vol" },
  {
    id: "modification",
    label: "Modification non autorisée",
    desc: "Altération des données",
  },
  {
    id: "divulgation",
    label: "Divulgation inappropriée",
    desc: "Communication non autorisée",
  },
  {
    id: "usage_detourne",
    label: "Usage détourné",
    desc: "Utilisation pour fins non prévues",
  },
  {
    id: "conservation_excessive",
    label: "Conservation excessive",
    desc: "Données conservées trop longtemps",
  },
  {
    id: "transfert_non_securise",
    label: "Transfert non sécurisé",
    desc: "Risques lors du transfert",
  },
  {
    id: "profilage",
    label: "Profilage non consenti",
    desc: "Décisions automatisées",
  },
  {
    id: "reidentification",
    label: "Ré-identification",
    desc: "Possibilité de ré-identifier des données anonymisées",
  },
];

const MESURES_TECHNIQUES = [
  "Chiffrement des données au repos",
  "Chiffrement en transit (HTTPS/TLS)",
  "Authentification multi-facteurs (MFA)",
  "Contrôle d'accès basé sur les rôles (RBAC)",
  "Journalisation des accès",
  "Sauvegardes chiffrées",
  "Pare-feu applicatif (WAF)",
  "Détection d'intrusion (IDS/IPS)",
  "Anonymisation des données",
  "Pseudonymisation",
  "Segmentation réseau",
  "VPN pour accès distant",
  "Effacement sécurisé",
];

const MESURES_ORGANISATIONNELLES = [
  "Formation du personnel",
  "Politique de confidentialité",
  "Ententes de confidentialité (employés)",
  "Processus de gestion des incidents",
  "Audits de sécurité réguliers",
  "Tests de pénétration",
  "Comité de gouvernance des données",
  "Documentation des traitements",
  "Évaluation des fournisseurs",
  "Plan de continuité",
  "Processus de réponse aux demandes d'accès",
];

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const EFVPManager = ({ showToast, entrepriseId }) => {
  const [efvps, setEfvps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterRisque, setFilterRisque] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingEFVP, setEditingEFVP] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [showDetail, setShowDetail] = useState(null);

  // ============================================
  // FORMULAIRE COMPLET LOI 25
  // ============================================
  const initialFormData = {
    // SECTION 1: IDENTIFICATION DU PROJET
    titre: "",
    projet: "",
    description: "",
    responsable: "",
    date_debut: new Date().toISOString().split("T")[0],
    date_fin_prevue: "",

    // SECTION 2: RENSEIGNEMENTS PERSONNELS
    types_rp: [],
    categories_personnes: [],
    volume_estime: "",
    donnees_sensibles: false,
    types_sensibles: [],

    // SECTION 3: FINALITÉS ET NÉCESSITÉ
    finalites: "",
    base_legale: "Consentement",
    necessite_justifiee: false,
    justification_necessite: "",
    minimisation_prevue: false,

    // SECTION 4: CYCLE DE VIE
    source_collecte: "",
    mode_collecte: "",
    duree_conservation: "",
    justification_duree: "",
    methode_destruction: "",

    // SECTION 5: COMMUNICATION ET TRANSFERT
    communication_interne: false,
    services_internes: "",
    communication_tiers: false,
    tiers_destinataires: "",
    finalite_communication: "",
    communication_hors_quebec: false,
    pays_destinataires: "",
    garanties_adequates: "",

    // SECTION 6: ANALYSE DES RISQUES
    risques_identifies: [],
    niveau_risque_global: "Moyen",
    justification_risque: "",

    // SECTION 7: MESURES D'ATTÉNUATION
    mesures_techniques: [],
    mesures_organisationnelles: [],
    mesures_contractuelles: "",
    risque_residuel: "Faible",

    // SECTION 8: DROITS DES PERSONNES
    mecanisme_consentement: "",
    info_fournie_collecte: false,
    droit_acces_prevu: false,
    droit_rectification_prevu: false,
    droit_suppression_prevu: false,
    droit_portabilite_prevu: false,
    processus_exercice_droits: "",

    // SECTION 9: CONSULTATION ET APPROBATION
    parties_consultees: "",
    date_consultation_rprp: "",
    avis_rprp: "",

    // SECTION 10: DÉCISION FINALE
    statut: "Non démarré",
    progression: 0,
    conclusions: "",
    recommandations: "",
    decision_finale: "",
    conditions_approbation: "",
    date_prochaine_revision: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const sections = [
    { id: 0, title: "Identification", icon: FileSearch },
    { id: 1, title: "Données personnelles", icon: Database },
    { id: 2, title: "Finalités", icon: Target },
    { id: 3, title: "Cycle de vie", icon: Clock },
    { id: 4, title: "Communication", icon: Globe },
    { id: 5, title: "Risques", icon: AlertTriangle },
    { id: 6, title: "Mesures", icon: Shield },
    { id: 7, title: "Droits", icon: Users },
    { id: 8, title: "Consultation", icon: FileText },
    { id: 9, title: "Décision", icon: CheckCircle },
  ];

  const niveauxRisque = ["Faible", "Moyen", "Élevé", "Critique"];
  const statuts = [
    "Non démarré",
    "En cours",
    "En révision",
    "Terminé",
    "Approuvé",
    "Refusé",
  ];
  const basesLegales = [
    "Consentement",
    "Exécution d'un contrat",
    "Obligation légale",
    "Intérêt légitime",
    "Protection des intérêts vitaux",
    "Mission d'intérêt public",
  ];

  const risqueColors = {
    Faible:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    Moyen:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    Élevé:
      "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
    Critique: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  };

  const statutColors = {
    "Non démarré":
      "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
    "En cours":
      "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
    "En révision":
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    Terminé:
      "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
    Approuvé:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    Refusé: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  };

  // ============================================
  // CHARGEMENT DES DONNÉES
  // ============================================
  useEffect(() => {
    if (entrepriseId) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [entrepriseId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await loadEFVPFromDB(entrepriseId);
      setEfvps(data);
    } catch (err) {
      setError("Erreur lors du chargement des EFVP.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // FILTRAGE
  // ============================================
  const filteredEfvps = efvps.filter((e) => {
    const matchSearch =
      (e.titre || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (e.projet || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (e.responsable || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatut = filterStatut === "all" || e.statut === filterStatut;
    const matchRisque =
      filterRisque === "all" || e.niveau_risque_global === filterRisque;
    return matchSearch && matchStatut && matchRisque;
  });

  // ============================================
  // HANDLERS
  // ============================================
  const handleAdd = () => {
    setEditingEFVP(null);
    setFormData(initialFormData);
    setActiveSection(0);
    setShowModal(true);
  };

  const handleEdit = (efvp) => {
    setEditingEFVP(efvp);
    setFormData({
      ...initialFormData,
      ...efvp,
      types_rp: efvp.types_rp || [],
      categories_personnes: efvp.categories_personnes || [],
      types_sensibles: efvp.types_sensibles || [],
      risques_identifies: efvp.risques_identifies || [],
      mesures_techniques: efvp.mesures_techniques || [],
      mesures_organisationnelles: efvp.mesures_organisationnelles || [],
    });
    setActiveSection(0);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titre.trim()) {
      showToast?.("Le titre est obligatoire.", "error");
      return;
    }

    if (!entrepriseId) {
      showToast?.("Erreur: Aucune entreprise sélectionnée.", "error");
      return;
    }

    setIsSaving(true);
    try {
      // Helper pour convertir "" en null
      const toNull = (val) =>
        val && typeof val === "string" && val.trim() !== "" ? val.trim() : null;

      const dataToSave = {
        titre: formData.titre.trim(),
        projet: toNull(formData.projet),
        description: toNull(formData.description),
        responsable: toNull(formData.responsable),
        date_debut: toNull(formData.date_debut),
        date_fin_prevue: toNull(formData.date_fin_prevue),
        types_rp: formData.types_rp || [],
        categories_personnes: formData.categories_personnes || [],
        volume_estime: toNull(formData.volume_estime),
        donnees_sensibles: formData.donnees_sensibles || false,
        types_sensibles: formData.types_sensibles || [],
        finalites: toNull(formData.finalites),
        base_legale: formData.base_legale || "Consentement",
        necessite_justifiee: formData.necessite_justifiee || false,
        justification_necessite: toNull(formData.justification_necessite),
        minimisation_prevue: formData.minimisation_prevue || false,
        source_collecte: toNull(formData.source_collecte),
        mode_collecte: toNull(formData.mode_collecte),
        duree_conservation: toNull(formData.duree_conservation),
        justification_duree: toNull(formData.justification_duree),
        methode_destruction: toNull(formData.methode_destruction),
        communication_interne: formData.communication_interne || false,
        services_internes: toNull(formData.services_internes),
        communication_tiers: formData.communication_tiers || false,
        tiers_destinataires: toNull(formData.tiers_destinataires),
        finalite_communication: toNull(formData.finalite_communication),
        communication_hors_quebec: formData.communication_hors_quebec || false,
        pays_destinataires: toNull(formData.pays_destinataires),
        garanties_adequates: toNull(formData.garanties_adequates),
        risques_identifies: formData.risques_identifies || [],
        niveau_risque_global: formData.niveau_risque_global || "Moyen",
        justification_risque: toNull(formData.justification_risque),
        mesures_techniques: formData.mesures_techniques || [],
        mesures_organisationnelles: formData.mesures_organisationnelles || [],
        mesures_contractuelles: toNull(formData.mesures_contractuelles),
        risque_residuel: formData.risque_residuel || "Faible",
        mecanisme_consentement: toNull(formData.mecanisme_consentement),
        info_fournie_collecte: formData.info_fournie_collecte || false,
        droit_acces_prevu: formData.droit_acces_prevu || false,
        droit_rectification_prevu: formData.droit_rectification_prevu || false,
        droit_suppression_prevu: formData.droit_suppression_prevu || false,
        droit_portabilite_prevu: formData.droit_portabilite_prevu || false,
        processus_exercice_droits: toNull(formData.processus_exercice_droits),
        parties_consultees: toNull(formData.parties_consultees),
        date_consultation_rprp: toNull(formData.date_consultation_rprp),
        avis_rprp: toNull(formData.avis_rprp),
        statut: formData.statut || "Non démarré",
        progression: formData.progression || 0,
        conclusions: toNull(formData.conclusions),
        recommandations: toNull(formData.recommandations),
        decision_finale: toNull(formData.decision_finale),
        conditions_approbation: toNull(formData.conditions_approbation),
        date_prochaine_revision: toNull(formData.date_prochaine_revision),
        entreprise_id: entrepriseId,
      };

      if (editingEFVP) {
        const updated = await updateEFVPInDB(editingEFVP.id, dataToSave);
        setEfvps(efvps.map((e) => (e.id === editingEFVP.id ? updated : e)));
        showToast?.("ÉFVP modifiée avec succès!", "success");
      } else {
        const newEfvp = await addEFVPToDB(dataToSave);
        setEfvps([newEfvp, ...efvps]);
        showToast?.("ÉFVP créée avec succès!", "success");
      }
      setShowModal(false);
    } catch (err) {
      console.error("Erreur détaillée:", err);
      showToast?.(
        `Erreur: ${err.message || "Erreur lors de l'enregistrement."}`,
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteEFVPFromDB(id);
      setEfvps(efvps.filter((e) => e.id !== id));
      showToast?.("ÉFVP supprimée.", "success");
    } catch (err) {
      showToast?.("Erreur lors de la suppression.", "error");
    }
    setShowDeleteConfirm(null);
  };

  // Toggle array items
  const toggleArrayItem = (field, item) => {
    const current = formData[field] || [];
    if (current.includes(item)) {
      setFormData({ ...formData, [field]: current.filter((i) => i !== item) });
    } else {
      setFormData({ ...formData, [field]: [...current, item] });
    }
  };

  // Calculer le pourcentage de complétion
  const calculateCompletion = () => {
    let filled = 0;
    let total = 10;

    if (formData.titre) filled++;
    if (formData.types_rp?.length > 0) filled++;
    if (formData.finalites) filled++;
    if (formData.duree_conservation) filled++;
    if (formData.risques_identifies?.length > 0) filled++;
    if (formData.mesures_techniques?.length > 0) filled++;
    if (formData.mesures_organisationnelles?.length > 0) filled++;
    if (formData.mecanisme_consentement) filled++;
    if (formData.parties_consultees) filled++;
    if (formData.conclusions) filled++;

    return Math.round((filled / total) * 100);
  };

  // ============================================
  // RENDU
  // ============================================
  if (!entrepriseId) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto text-amber-500 mb-4" size={48} />
        <p className="text-gray-600 dark:text-gray-400">
          Veuillez sélectionner une entreprise.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={40} className="text-sky-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <FileSearch className="text-purple-600" />
            Évaluations des facteurs vie privée
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Article 3.3 de la Loi 25 — Obligatoire avant tout projet impliquant
            des RP
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition font-medium shadow-lg"
        >
          <Plus size={20} />
          Nouvelle ÉFVP
        </button>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl"
        >
          <option value="all">Tous les statuts</option>
          {statuts.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={filterRisque}
          onChange={(e) => setFilterRisque(e.target.value)}
          className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl"
        >
          <option value="all">Tous les risques</option>
          {niveauxRisque.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <button
          onClick={loadData}
          className="p-2.5 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
          title="Actualiser"
        >
          <RefreshCw size={20} className="text-gray-600 dark:text-gray-300" />
        </button>
      </div>

      {/* Liste des EFVP */}
      {error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <AlertTriangle className="text-red-500 mx-auto mb-2" size={32} />
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      ) : filteredEfvps.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-12 text-center">
          <FileSearch className="text-gray-400 mx-auto mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
            Aucune ÉFVP
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Créez votre première évaluation des facteurs relatifs à la vie
            privée.
          </p>
          <button
            onClick={handleAdd}
            className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Plus size={18} className="inline mr-2" />
            Nouvelle ÉFVP
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredEfvps.map((efvp) => (
            <div
              key={efvp.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {efvp.titre}
                      </h3>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          risqueColors[efvp.niveau_risque_global] ||
                          risqueColors.Moyen
                        }`}
                      >
                        Risque {efvp.niveau_risque_global || "Moyen"}
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          statutColors[efvp.statut] ||
                          statutColors["Non démarré"]
                        }`}
                      >
                        {efvp.statut || "Non démarré"}
                      </span>
                    </div>

                    {efvp.projet && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Projet: {efvp.projet}
                      </p>
                    )}

                    <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                      {efvp.responsable && (
                        <span className="flex items-center gap-1">
                          <User size={14} />
                          {efvp.responsable}
                        </span>
                      )}
                      {efvp.date_debut && (
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(efvp.date_debut).toLocaleDateString(
                            "fr-CA"
                          )}
                        </span>
                      )}
                      {efvp.types_rp?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Database size={14} />
                          {efvp.types_rp.length} type(s) de RP
                        </span>
                      )}
                      {efvp.communication_hors_quebec && (
                        <span className="flex items-center gap-1 text-amber-600">
                          <Globe size={14} />
                          Transfert hors QC
                        </span>
                      )}
                    </div>

                    {/* Barre de progression */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                        <span>Progression</span>
                        <span>{efvp.progression || 0}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-purple-500 transition-all"
                          style={{ width: `${efvp.progression || 0}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        setShowDetail(showDetail === efvp.id ? null : efvp.id)
                      }
                      className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition"
                      title="Voir détails"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => handleEdit(efvp)}
                      className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/30 rounded-lg transition"
                      title="Modifier"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(efvp.id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition"
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Détails dépliables */}
                {showDetail === efvp.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid md:grid-cols-2 gap-4 text-sm">
                    {efvp.finalites && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Finalités:
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {efvp.finalites}
                        </p>
                      </div>
                    )}
                    {efvp.risques_identifies?.length > 0 && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Risques identifiés:
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {efvp.risques_identifies.join(", ")}
                        </p>
                      </div>
                    )}
                    {efvp.conclusions && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Conclusions:
                        </span>
                        <p className="text-gray-600 dark:text-gray-400">
                          {efvp.conclusions}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Confirmation suppression */}
              {showDeleteConfirm === efvp.id && (
                <div className="bg-red-50 dark:bg-red-900/20 p-4 border-t border-red-200 dark:border-red-800">
                  <p className="text-red-700 dark:text-red-300 mb-3">
                    Supprimer cette ÉFVP ?
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDelete(efvp.id)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Supprimer
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(null)}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ============================================ */}
      {/* MODAL FORMULAIRE COMPLET */}
      {/* ============================================ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b dark:border-gray-700 flex items-center justify-between bg-purple-50 dark:bg-purple-900/20">
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {editingEFVP ? "Modifier l'ÉFVP" : "Nouvelle ÉFVP"}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Complétude: {calculateCompletion()}%
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            {/* Navigation sections */}
            <div className="px-4 py-3 border-b dark:border-gray-700 overflow-x-auto">
              <div className="flex gap-1 min-w-max">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                      activeSection === section.id
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    }`}
                  >
                    <section.icon size={16} />
                    <span className="hidden md:inline">{section.title}</span>
                    <span className="md:hidden">{section.id + 1}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Contenu du formulaire */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* SECTION 0: IDENTIFICATION */}
                {activeSection === 0 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <FileSearch size={20} className="text-purple-600" />
                      Identification du projet
                    </h4>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Titre de l'ÉFVP *
                        </label>
                        <input
                          type="text"
                          value={formData.titre}
                          onChange={(e) =>
                            setFormData({ ...formData, titre: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: Migration vers Microsoft 365"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Nom du projet
                        </label>
                        <input
                          type="text"
                          value={formData.projet}
                          onChange={(e) =>
                            setFormData({ ...formData, projet: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: Transformation numérique"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description du projet
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Décrivez le projet et son contexte..."
                      />
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Responsable
                        </label>
                        <input
                          type="text"
                          value={formData.responsable}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              responsable: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Nom du responsable"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Date de début
                        </label>
                        <input
                          type="date"
                          value={formData.date_debut}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_debut: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Date fin prévue
                        </label>
                        <input
                          type="date"
                          value={formData.date_fin_prevue}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_fin_prevue: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* SECTION 1: RENSEIGNEMENTS PERSONNELS */}
                {activeSection === 1 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Database size={20} className="text-purple-600" />
                      Renseignements personnels concernés
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Types de renseignements personnels collectés
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {TYPES_RP.map((type) => (
                          <label
                            key={type}
                            className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <input
                              type="checkbox"
                              checked={formData.types_rp?.includes(type)}
                              onChange={() => toggleArrayItem("types_rp", type)}
                              className="rounded border-gray-300 text-purple-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {type}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Catégories de personnes concernées
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {CATEGORIES_PERSONNES.map((cat) => (
                          <label
                            key={cat}
                            className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <input
                              type="checkbox"
                              checked={formData.categories_personnes?.includes(
                                cat
                              )}
                              onChange={() =>
                                toggleArrayItem("categories_personnes", cat)
                              }
                              className="rounded border-gray-300 text-purple-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {cat}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Volume estimé de personnes concernées
                      </label>
                      <input
                        type="text"
                        value={formData.volume_estime}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            volume_estime: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Ex: 500-1000 clients"
                      />
                    </div>

                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                      <label className="flex items-center gap-3 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={formData.donnees_sensibles}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              donnees_sensibles: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-amber-600"
                        />
                        <span className="font-medium text-amber-800 dark:text-amber-300">
                          Ce projet implique des données sensibles (Art. 12)
                        </span>
                      </label>

                      {formData.donnees_sensibles && (
                        <div className="ml-8 grid grid-cols-2 gap-2">
                          {TYPES_DONNEES_SENSIBLES.map((type) => (
                            <label
                              key={type.id}
                              className="flex items-center gap-2 text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={formData.types_sensibles?.includes(
                                  type.id
                                )}
                                onChange={() =>
                                  toggleArrayItem("types_sensibles", type.id)
                                }
                                className="rounded border-gray-300 text-amber-600"
                              />
                              <span className="text-gray-700 dark:text-gray-300">
                                {type.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* SECTION 2: FINALITÉS */}
                {activeSection === 2 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Target size={20} className="text-purple-600" />
                      Finalités et nécessité
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Finalités de la collecte *
                      </label>
                      <textarea
                        value={formData.finalites}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            finalites: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Pourquoi ces renseignements sont-ils collectés? Quels sont les objectifs?"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Base légale du traitement
                      </label>
                      <select
                        value={formData.base_legale}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            base_legale: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                      >
                        {basesLegales.map((b) => (
                          <option key={b} value={b}>
                            {b}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.necessite_justifiee}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              necessite_justifiee: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-purple-600"
                        />
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Nécessité justifiée
                          </span>
                          <p className="text-xs text-gray-500">
                            La collecte est strictement nécessaire aux finalités
                            identifiées
                          </p>
                        </div>
                      </label>

                      {formData.necessite_justifiee && (
                        <textarea
                          value={formData.justification_necessite}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              justification_necessite: e.target.value,
                            })
                          }
                          rows={2}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Justifiez pourquoi cette collecte est nécessaire..."
                        />
                      )}

                      <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.minimisation_prevue}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              minimisation_prevue: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-purple-600"
                        />
                        <div>
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Minimisation des données
                          </span>
                          <p className="text-xs text-gray-500">
                            Seuls les RP strictement nécessaires seront
                            collectés
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* SECTION 3: CYCLE DE VIE */}
                {activeSection === 3 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Clock size={20} className="text-purple-600" />
                      Cycle de vie des données
                    </h4>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Source de collecte
                        </label>
                        <input
                          type="text"
                          value={formData.source_collecte}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              source_collecte: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: Formulaire web, API partenaire..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Mode de collecte
                        </label>
                        <input
                          type="text"
                          value={formData.mode_collecte}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              mode_collecte: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: Directement auprès de la personne, tiers..."
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Durée de conservation
                        </label>
                        <input
                          type="text"
                          value={formData.duree_conservation}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              duree_conservation: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: 7 ans après la fin de la relation"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Méthode de destruction
                        </label>
                        <input
                          type="text"
                          value={formData.methode_destruction}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              methode_destruction: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Ex: Effacement sécurisé, déchiquetage..."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Justification de la durée
                      </label>
                      <textarea
                        value={formData.justification_duree}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            justification_duree: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Pourquoi cette durée? (obligations légales, besoins opérationnels...)"
                      />
                    </div>
                  </div>
                )}

                {/* SECTION 4: COMMUNICATION */}
                {activeSection === 4 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Globe size={20} className="text-purple-600" />
                      Communication et transfert
                    </h4>

                    {/* Communication interne */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <label className="flex items-center gap-3 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={formData.communication_interne}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              communication_interne: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-purple-600"
                        />
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Communication à d'autres services internes
                        </span>
                      </label>
                      {formData.communication_interne && (
                        <input
                          type="text"
                          value={formData.services_internes}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              services_internes: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-lg"
                          placeholder="Quels services? (RH, Comptabilité, Marketing...)"
                        />
                      )}
                    </div>

                    {/* Communication tiers */}
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <label className="flex items-center gap-3 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={formData.communication_tiers}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              communication_tiers: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-purple-600"
                        />
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          Communication à des tiers
                        </span>
                      </label>
                      {formData.communication_tiers && (
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={formData.tiers_destinataires}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                tiers_destinataires: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-lg"
                            placeholder="Quels tiers? (fournisseurs, partenaires...)"
                          />
                          <input
                            type="text"
                            value={formData.finalite_communication}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                finalite_communication: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-lg"
                            placeholder="Pour quelle finalité?"
                          />
                        </div>
                      )}
                    </div>

                    {/* Transfert hors Québec */}
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border-l-4 border-amber-500">
                      <label className="flex items-center gap-3 cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={formData.communication_hors_quebec}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              communication_hors_quebec: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-amber-600"
                        />
                        <div>
                          <span className="font-medium text-amber-800 dark:text-amber-300">
                            Transfert hors du Québec (Art. 17)
                          </span>
                          <p className="text-xs text-amber-700 dark:text-amber-400">
                            Requiert une évaluation des garanties adéquates
                          </p>
                        </div>
                      </label>
                      {formData.communication_hors_quebec && (
                        <div className="space-y-3 mt-3">
                          <input
                            type="text"
                            value={formData.pays_destinataires}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                pays_destinataires: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2 border border-amber-300 dark:border-amber-600 dark:bg-gray-700 dark:text-white rounded-lg"
                            placeholder="Quels pays ou provinces?"
                          />
                          <textarea
                            value={formData.garanties_adequates}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                garanties_adequates: e.target.value,
                              })
                            }
                            rows={2}
                            className="w-full px-4 py-2 border border-amber-300 dark:border-amber-600 dark:bg-gray-700 dark:text-white rounded-lg"
                            placeholder="Quelles garanties sont en place? (clauses contractuelles, certifications...)"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* SECTION 5: RISQUES */}
                {activeSection === 5 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <AlertTriangle size={20} className="text-purple-600" />
                      Analyse des risques
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Risques identifiés
                      </label>
                      <div className="grid md:grid-cols-2 gap-2">
                        {RISQUES_TYPES.map((risque) => (
                          <label
                            key={risque.id}
                            className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <input
                              type="checkbox"
                              checked={formData.risques_identifies?.includes(
                                risque.id
                              )}
                              onChange={() =>
                                toggleArrayItem("risques_identifies", risque.id)
                              }
                              className="mt-1 rounded border-gray-300 text-red-600"
                            />
                            <div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {risque.label}
                              </span>
                              <p className="text-xs text-gray-500">
                                {risque.desc}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Niveau de risque global
                      </label>
                      <select
                        value={formData.niveau_risque_global}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            niveau_risque_global: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                      >
                        {niveauxRisque.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Justification du niveau de risque
                      </label>
                      <textarea
                        value={formData.justification_risque}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            justification_risque: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Expliquez pourquoi ce niveau de risque a été attribué..."
                      />
                    </div>
                  </div>
                )}

                {/* SECTION 6: MESURES */}
                {activeSection === 6 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Shield size={20} className="text-purple-600" />
                      Mesures d'atténuation
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mesures techniques
                      </label>
                      <div className="grid md:grid-cols-2 gap-2">
                        {MESURES_TECHNIQUES.map((mesure) => (
                          <label
                            key={mesure}
                            className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <input
                              type="checkbox"
                              checked={formData.mesures_techniques?.includes(
                                mesure
                              )}
                              onChange={() =>
                                toggleArrayItem("mesures_techniques", mesure)
                              }
                              className="rounded border-gray-300 text-green-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {mesure}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mesures organisationnelles
                      </label>
                      <div className="grid md:grid-cols-2 gap-2">
                        {MESURES_ORGANISATIONNELLES.map((mesure) => (
                          <label
                            key={mesure}
                            className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                          >
                            <input
                              type="checkbox"
                              checked={formData.mesures_organisationnelles?.includes(
                                mesure
                              )}
                              onChange={() =>
                                toggleArrayItem(
                                  "mesures_organisationnelles",
                                  mesure
                                )
                              }
                              className="rounded border-gray-300 text-green-600"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {mesure}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Mesures contractuelles
                      </label>
                      <textarea
                        value={formData.mesures_contractuelles}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mesures_contractuelles: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Clauses avec fournisseurs, ententes de confidentialité..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Risque résiduel après mesures
                      </label>
                      <select
                        value={formData.risque_residuel}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            risque_residuel: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                      >
                        {niveauxRisque.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* SECTION 7: DROITS */}
                {activeSection === 7 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <Users size={20} className="text-purple-600" />
                      Droits des personnes concernées
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Mécanisme de consentement
                      </label>
                      <textarea
                        value={formData.mecanisme_consentement}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mecanisme_consentement: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Comment le consentement est-il obtenu? (formulaire, case à cocher...)"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Droits prévus
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.info_fournie_collecte}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              info_fournie_collecte: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-purple-600"
                        />
                        <span className="text-gray-700 dark:text-gray-300">
                          Information fournie au moment de la collecte
                        </span>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.droit_acces_prevu}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              droit_acces_prevu: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-purple-600"
                        />
                        <span className="text-gray-700 dark:text-gray-300">
                          Droit d'accès
                        </span>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.droit_rectification_prevu}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              droit_rectification_prevu: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-purple-600"
                        />
                        <span className="text-gray-700 dark:text-gray-300">
                          Droit de rectification
                        </span>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.droit_suppression_prevu}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              droit_suppression_prevu: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-purple-600"
                        />
                        <span className="text-gray-700 dark:text-gray-300">
                          Droit à l'effacement (droit à l'oubli)
                        </span>
                      </label>

                      <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.droit_portabilite_prevu}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              droit_portabilite_prevu: e.target.checked,
                            })
                          }
                          className="w-5 h-5 rounded border-gray-300 text-purple-600"
                        />
                        <span className="text-gray-700 dark:text-gray-300">
                          Droit à la portabilité
                        </span>
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Processus d'exercice des droits
                      </label>
                      <textarea
                        value={formData.processus_exercice_droits}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            processus_exercice_droits: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Comment les personnes peuvent-elles exercer leurs droits?"
                      />
                    </div>
                  </div>
                )}

                {/* SECTION 8: CONSULTATION */}
                {activeSection === 8 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <FileText size={20} className="text-purple-600" />
                      Consultation et avis
                    </h4>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Parties consultées
                      </label>
                      <textarea
                        value={formData.parties_consultees}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            parties_consultees: e.target.value,
                          })
                        }
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Ex: Responsable TI, Conseiller juridique, DPO..."
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Date de consultation du RPRP
                        </label>
                        <input
                          type="date"
                          value={formData.date_consultation_rprp}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_consultation_rprp: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          RPRP = Responsable de la protection des renseignements
                          personnels
                        </p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Avis du RPRP
                      </label>
                      <textarea
                        value={formData.avis_rprp}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            avis_rprp: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Avis et recommandations du responsable..."
                      />
                    </div>
                  </div>
                )}

                {/* SECTION 9: DÉCISION */}
                {activeSection === 9 && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <CheckCircle size={20} className="text-purple-600" />
                      Décision finale
                    </h4>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Statut
                        </label>
                        <select
                          value={formData.statut}
                          onChange={(e) =>
                            setFormData({ ...formData, statut: e.target.value })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        >
                          {statuts.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Progression (%)
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="10"
                          value={formData.progression}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              progression: parseInt(e.target.value),
                            })
                          }
                          className="w-full"
                        />
                        <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                          {formData.progression}%
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Conclusions
                      </label>
                      <textarea
                        value={formData.conclusions}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            conclusions: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Résumé des conclusions de l'évaluation..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Recommandations
                      </label>
                      <textarea
                        value={formData.recommandations}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            recommandations: e.target.value,
                          })
                        }
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        placeholder="Recommandations pour le projet..."
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Décision finale
                        </label>
                        <select
                          value={formData.decision_finale}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              decision_finale: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        >
                          <option value="">-- Sélectionner --</option>
                          <option value="Approuvé">✅ Approuvé</option>
                          <option value="Approuvé avec conditions">
                            ⚠️ Approuvé avec conditions
                          </option>
                          <option value="Refusé">❌ Refusé</option>
                          <option value="En attente">
                            ⏳ En attente d'informations
                          </option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Prochaine révision
                        </label>
                        <input
                          type="date"
                          value={formData.date_prochaine_revision}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_prochaine_revision: e.target.value,
                            })
                          }
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg"
                        />
                      </div>
                    </div>

                    {formData.decision_finale ===
                      "Approuvé avec conditions" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Conditions d'approbation
                        </label>
                        <textarea
                          value={formData.conditions_approbation}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              conditions_approbation: e.target.value,
                            })
                          }
                          rows={2}
                          className="w-full px-4 py-2 border border-amber-300 dark:border-amber-600 dark:bg-gray-700 dark:text-white rounded-lg"
                          placeholder="Quelles conditions doivent être remplies?"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer avec navigation et boutons */}
              <div className="px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setActiveSection(Math.max(0, activeSection - 1))
                    }
                    disabled={activeSection === 0}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                  >
                    ← Précédent
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setActiveSection(
                        Math.min(sections.length - 1, activeSection + 1)
                      )
                    }
                    disabled={activeSection === sections.length - 1}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                  >
                    Suivant →
                  </button>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-5 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        {editingEFVP ? "Modifier" : "Créer l'ÉFVP"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EFVPManager;
